import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { IconButton } from '@mui/material';
import SimpleScrollList from '../SimpleScrollList/SimpleScrollList';
import SimpleHorizontalList from '../SimpleScrollList/SimpleHorizontalList';
import EventExpandedView from '../PlaceDetails/EventExpandedView';
import ReviewerCard from './ReviewerCard';
import ReviewerSearchBar from './ReviewerSearchBar';
import ReviewerSuggestions from './ReviewerSuggestions';
import { useReviewerItems, useSuggestedReviewers, useReviewerSearch } from '../../hooks/useReviewerItems';

const EMPTY_ITEMS = [];

/** Midnight today (local) — an event running today still counts as upcoming. */
const startOfToday = () => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d.getTime();
};

/** Which bucket an item belongs to. The API tags every row with `_source`. */
const sourceOf = (item) => item?._source
  || (item?.review_id ? 'review' : item?.venue_id ? 'venue' : 'event');

/**
 * Events are kept only while they are still ahead of us — an event that has
 * already finished is of no use to someone browsing a reviewer.
 * Multi-day events stay until their end date; undated rows can't be proven
 * past, so they are kept.
 */
const isUpcomingEvent = (item, todayStart) => {
  const raw = item?.event_end_date || item?.event_date;
  if (!raw) return true;
  const t = new Date(raw).getTime();
  if (Number.isNaN(t)) return true;
  return t >= todayStart;
};

const matchesFilter = (item, filter, todayStart) => {
  const src = sourceOf(item);
  if (filter === 'events') return src === 'event' && isUpcomingEvent(item, todayStart);
  if (filter === 'reviews') return src === 'review';
  if (filter === 'venues') return src === 'venue';
  return true;
};

/**
 * ReviewerPanel — content of the ผู้รีวิว (Reviewers) tab.
 *
 * Layout, top to bottom:
 *   1. suggestion strip (3 visible, horizontally scrollable, up to 20 random)
 *   2. search box with magnifying-glass button
 *   3a. search results — list of purple reviewer cards with ติดตาม, or
 *   3b. the selected reviewer's card + their reviews/events list
 *
 * The item list is reported upward through onItemsChange so the map renders
 * exactly the same markers and info boxes as the other tabs.
 */
const ReviewerPanel = ({
  selectedReviewer,          // { id, name, ... } or null
  onSelectReviewer,          // (reviewer) => void
  onClearReviewer,           // () => void
  profile = null,            // optional /users/:id payload for richer card data
  language = 'TH',
  initialFilter = null,      // category filter carried in by a shared link
  layout = 'vertical',       // 'vertical' | 'horizontal'
  childClicked = null,
  onLocationClick = null,
  onActiveIndexChange = null,
  onItemsChange = null,
  tracker = null,
}) => {
  const [expandedPlace, setExpandedPlace] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  // 'events' | 'reviews' | 'venues' | null (= show everything)
  const [typeFilter, setTypeFilter] = useState(initialFilter || null);

  const {
    reviewers: suggested,
    loading: suggestedLoading,
    loadingMore,
    hasMore,
    loadMore,
  } = useSuggestedReviewers(20, true);
  const { results, loading: searching, hasSearched, search, reset } = useReviewerSearch();

  // Merge the profile payload (avatar/bio) into the reviewer we display
  const displayReviewer = useMemo(() => {
    if (!selectedReviewer) return null;
    const p = profile?.user || null;
    return {
      ...selectedReviewer,
      name: p?.name || selectedReviewer.name,
      avatar_url: p?.avatar_url || selectedReviewer.avatar_url || null,
      bio: p?.bio || selectedReviewer.bio || null,
    };
  }, [selectedReviewer, profile]);

  // The profile name is the reliable one — map info boxes may pass a placeholder
  const { items, loading: itemsLoading, error } = useReviewerItems(
    selectedReviewer?.id || null,
    language,
    displayReviewer?.name || null
  );

  // A filter belongs to the reviewer it was chosen for. `initialFilter` is set
  // only while a shared link is being opened, and Home clears it as soon as the
  // user picks a different reviewer — so this falls back to "show everything".
  useEffect(() => {
    setTypeFilter(initialFilter || null);
  }, [selectedReviewer?.id, initialFilter]);

  // Counts shown on the filter buttons. กิจกรรม counts upcoming events only,
  // so the number always matches what the filter would actually show.
  const filterCounts = useMemo(() => {
    const todayStart = startOfToday();
    const counts = { events: 0, reviews: 0, venues: 0 };
    items.forEach((item) => {
      const src = sourceOf(item);
      if (src === 'event') {
        if (isUpcomingEvent(item, todayStart)) counts.events += 1;
      } else if (src === 'review') {
        counts.reviews += 1;
      } else if (src === 'venue') {
        counts.venues += 1;
      }
    });
    return counts;
  }, [items]);

  // What the list AND the map show. Filtering here keeps the two in sync.
  const visibleItems = useMemo(() => {
    if (!typeFilter) return items;
    const todayStart = startOfToday();
    return items.filter((item) => matchesFilter(item, typeFilter, todayStart));
  }, [items, typeFilter]);

  // Publish items to the parent so the map can render this reviewer's pins.
  // Guarded by a ref so an inline parent callback can't cause a render loop.
  const publishedRef = useRef(null);
  useEffect(() => {
    if (!onItemsChange) return;
    const next = selectedReviewer ? visibleItems : EMPTY_ITEMS;
    if (publishedRef.current === next) return;
    publishedRef.current = next;
    onItemsChange(next);
  }, [visibleItems, selectedReviewer, onItemsChange]);

  const handleSelect = (reviewer) => {
    setExpandedPlace(null);
    setTypeFilter(null);
    if (onSelectReviewer) onSelectReviewer(reviewer);
  };

  const handleBackToResults = () => {
    setExpandedPlace(null);
    if (onClearReviewer) onClearReviewer();
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    reset();
  };

  // Infinite scroll for the reviewer directory (20 more at the end of the list)
  const listRef = useRef(null);
  const handleDirectoryScroll = (e) => {
    if (hasSearched || !hasMore || loadingMore) return;
    const el = e.currentTarget;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 200) loadMore();
  };

  const ListComponent = layout === 'horizontal' ? SimpleHorizontalList : SimpleScrollList;

  const emptyText = language === 'TH'
    ? 'ยังไม่มีรายการของผู้รีวิวคนนี้'
    : language === 'CN' ? '该评论者暂无内容'
      : 'This reviewer has no items yet';

  const emptyFilterText = typeFilter === 'events'
    ? (language === 'TH' ? 'ไม่มีกิจกรรมที่กำลังจะมาถึงของผู้รีวิวคนนี้'
      : language === 'CN' ? '该评论者暂无即将举行的活动'
        : 'This reviewer has no upcoming events')
    : (language === 'TH' ? 'ไม่มีรายการในหมวดนี้'
      : language === 'CN' ? '该分类暂无内容'
        : 'No items in this category');

  const noResultsText = language === 'TH'
    ? 'ไม่พบผู้รีวิวที่ค้นหา'
    : language === 'CN' ? '未找到评论者'
      : 'No reviewers found';

  const hintText = language === 'TH'
    ? 'เลือกผู้รีวิวจากรายการแนะนำ หรือค้นหาชื่อผู้รีวิวที่ต้องการ'
    : language === 'CN' ? '从推荐中选择评论者，或搜索评论者姓名'
      : 'Pick a suggested reviewer, or search for one by name';

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      {/* --- Header: suggestions + search ------------------------------ */}
      <Box sx={{ flexShrink: 0, px: 1.5, pt: 1.5, pb: 1 }}>
        <ReviewerSuggestions
          reviewers={suggested.slice(0, 20)}
          loading={suggestedLoading}
          onSelect={handleSelect}
          language={language}
          selectedId={selectedReviewer?.id || null}
          sx={{ mb: 1 }}
        />

        <ReviewerSearchBar
          value={searchTerm}
          onChange={setSearchTerm}
          onSearch={search}
          onClear={handleClearSearch}
          loading={searching}
          language={language}
        />
      </Box>

      {/* --- Selected reviewer card ------------------------------------ */}
      {displayReviewer && (
        <Box sx={{ flexShrink: 0, px: 1.5, pb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {hasSearched && (
            <IconButton
              size="small"
              onClick={handleBackToResults}
              aria-label="Back to reviewer results"
              sx={{ color: '#7c3aed', flexShrink: 0 }}
            >
              <ArrowBackIcon sx={{ fontSize: 18 }} />
            </IconButton>
          )}
          <ReviewerCard
            reviewer={displayReviewer}
            language={language}
            itemCount={visibleItems.length}
            onClose={onClearReviewer}
            showShare
            showFilters
            activeFilter={typeFilter}
            onFilterChange={setTypeFilter}
            filterCounts={filterCounts}
            sx={{ flex: 1, minWidth: 0 }}
          />
        </Box>
      )}

      {/* --- Body ------------------------------------------------------ */}
      <Box sx={{ flexGrow: 1, minHeight: 0, position: 'relative' }}>
        {expandedPlace && (
          <EventExpandedView place={expandedPlace} onClose={() => setExpandedPlace(null)} />
        )}

        {/* Reviewer directory — search results, or the paged suggestion list */}
        {!displayReviewer && (
          <Box
            ref={listRef}
            onScroll={handleDirectoryScroll}
            sx={{ height: '100%', overflowY: 'auto', px: 1.5, pb: 2 }}
          >
            {hasSearched ? (
              searching ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', pt: 4 }}>
                  <CircularProgress size={26} sx={{ color: '#7c3aed' }} />
                </Box>
              ) : results.length === 0 ? (
                <Typography sx={{ textAlign: 'center', color: 'text.secondary', fontSize: '0.85rem', pt: 4 }}>
                  {noResultsText}
                </Typography>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {results.map((reviewer) => (
                    <ReviewerCard
                      key={`s-${reviewer.id}`}
                      reviewer={reviewer}
                      language={language}
                      onClick={() => handleSelect(reviewer)}
                    />
                  ))}
                </Box>
              )
            ) : suggestedLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', pt: 4 }}>
                <CircularProgress size={26} sx={{ color: '#7c3aed' }} />
              </Box>
            ) : suggested.length === 0 ? (
              <Typography sx={{ textAlign: 'center', color: 'text.secondary', fontSize: '0.85rem', px: 3, pt: 4 }}>
                {hintText}
              </Typography>
            ) : (
              <>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {suggested.map((reviewer) => (
                    <ReviewerCard
                      key={`d-${reviewer.id}`}
                      reviewer={reviewer}
                      language={language}
                      onClick={() => handleSelect(reviewer)}
                    />
                  ))}
                </Box>

                {loadingMore && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                    <CircularProgress size={22} sx={{ color: '#7c3aed' }} />
                  </Box>
                )}

                {!hasMore && (
                  <Typography sx={{ textAlign: 'center', color: 'text.secondary', fontSize: '0.72rem', py: 2 }}>
                    {language === 'TH' ? 'แสดงครบทุกคนแล้ว'
                      : language === 'CN' ? '已显示全部'
                        : 'End of list'}
                  </Typography>
                )}
              </>
            )}
          </Box>
        )}

        {/* Selected reviewer's items */}
        {displayReviewer && (
          itemsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', pt: 4 }}>
              <CircularProgress size={26} sx={{ color: '#7c3aed' }} />
            </Box>
          ) : visibleItems.length === 0 ? (
            <Typography sx={{ textAlign: 'center', color: 'text.secondary', fontSize: '0.85rem', pt: 4, px: 3 }}>
              {items.length === 0 ? (error || emptyText) : emptyFilterText}
            </Typography>
          ) : (
            <ListComponent
              key={`reviewer-items-${typeFilter || 'all'}`}
              places={visibleItems}
              childClicked={childClicked}
              isLoading={false}
              onLocationClick={onLocationClick}
              language={language}
              onActiveIndexChange={onActiveIndexChange}
              tracker={tracker}
              hasSearched={true}
              context="reviewer"
              feedMode={false}
              showPager={false}
              showCounter={false}
              onExpand={setExpandedPlace}
              onCreatorClick={handleSelect}
            />
          )
        )}
      </Box>
    </Box>
  );
};

export default ReviewerPanel;
